import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import AudioFileRoundedIcon from "@mui/icons-material/AudioFileRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { LanguageContext } from "../context/LanguageContext";
import { useAuth } from "../hooks/AuthProvider";
import { useAiVoiceAssistantSettings } from "../context/AiVoiceAssistantContext";
import {
  AI_CHAT_VOICE_CONFIG_UPDATED_EVENT,
  deleteAssistantChat,
  fetchAiChatVoiceReplyConfig,
  fetchAssistantChatHistory,
  normalizeAiChatVoiceReplyConfig,
  resolveAiChatVoiceForUser,
  saveAiChatVoicePreference,
  processVoiceCommand,
  resetVoiceSession,
  sendAssistantChatMessage,
  sendAssistantVoiceChatMessage,
  startNewAssistantChat,
} from "../services/aiVoiceAssistantApi";
import aiVoiceAssistantTranslations from "../languages/aiVoiceAssistant.json";
import "./AiVoiceAssistant.css";

const CONVERSATION_STORAGE_PREFIX = "sama-ai-voice-conversation";

const MIME_TYPE_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

const DEFAULT_TEXT = aiVoiceAssistantTranslations.en.aiVoiceAssistant;

const getPreferredMimeType = () => {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  return MIME_TYPE_CANDIDATES.find((type) => window.MediaRecorder.isTypeSupported(type)) || "";
};

const createId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createUuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const getPreviewTitle = (source, label, text) => {
  if (source === "upload" && label) return label;
  if (source === "recording") return text.liveRecording;
  return "";
};

const formatDuration = (ms) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const readAudioDuration = (url) =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || !url) {
      resolve(0);
      return;
    }

    const audio = new window.Audio();

    const finish = (duration) => {
      audio.removeAttribute("src");
      audio.load();
      resolve(Number.isFinite(duration) ? Math.max(0, Math.round(duration * 1000)) : 0);
    };

    audio.preload = "metadata";
    audio.onloadedmetadata = () => finish(audio.duration);
    audio.onerror = () => finish(0);
    audio.src = url;
  });

const getConversationStorageKey = (userId, organizationId) => {
  const safeUserId = userId || "anonymous";
  const safeOrgId = organizationId || "no-organization";
  return `${CONVERSATION_STORAGE_PREFIX}:${safeUserId}:${safeOrgId}`;
};

const normalizeChatSessionId = (value) => {
  const normalized = String(value || "").trim();
  return normalized && normalized.length <= 255 ? normalized : "";
};

const readConversation = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) {
    return { key: storageKey, sessionId: "", messages: [] };
  }

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return { key: storageKey, sessionId: "", messages: [] };
    }

    const parsed = JSON.parse(raw);
    // Migration from the previous implementation: RAG used chatSessionId while
    // voice commands used sessionId. The new AI collection requires one shared
    // session_id when the voice-command context bridge is expected to work.
    const sharedSessionId =
      normalizeChatSessionId(parsed?.chatSessionId) || normalizeChatSessionId(parsed?.sessionId);

    return {
      key: storageKey,
      sessionId: sharedSessionId,
      messages: Array.isArray(parsed?.messages) ? parsed.messages : [],
    };
  } catch (error) {
    console.error("Failed to read AI assistant conversation:", error);
    return { key: storageKey, sessionId: "", messages: [] };
  }
};

const chatHistoryToMessages = (history, sessionId) =>
  (Array.isArray(history) ? history : []).flatMap((turn, index) => {
    const source = turn && typeof turn === "object" ? turn : {};
    const question = String(source.question || source.prompt || "").trim();
    const answer = String(source.answer || source.response || source.reply || "").trim();
    const createdAt = source.created_at || source.createdAt || new Date().toISOString();
    const messages = [];

    if (question) {
      messages.push({
        id: `rag-history-${sessionId || "latest"}-${index}-user`,
        role: "user",
        text: question,
        mode: "chat",
        createdAt,
      });
    }

    if (answer) {
      messages.push({
        id: `rag-history-${sessionId || "latest"}-${index}-assistant`,
        role: "assistant",
        text: answer,
        sources: Array.isArray(source.sources) ? source.sources : [],
        mode: "chat",
        createdAt,
      });
    }

    return messages;
  });

const getReadableError = (error, text = DEFAULT_TEXT) => {
  if (error?.name === "NotAllowedError" || error?.name === "SecurityError") {
    return text.microphoneBlocked;
  }

  const data = error?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message.trim();
  if (typeof data?.error === "string" && data.error.trim()) return data.error.trim();
  if (typeof error?.message === "string" && error.message.trim()) return error.message.trim();

  return text.processFailed;
};

const formatAudioTime = (seconds) => {
  const safeSeconds = Number.isFinite(Number(seconds)) ? Math.max(0, Math.floor(Number(seconds))) : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
};

function AssistantAudioPlayer({ src, text, autoPlay = false, onAutoPlayAttempted }) {
  const audioRef = useRef(null);
  const autoplayAttemptedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loop, setLoop] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  const pauseOtherAssistantAudio = useCallback(() => {
    if (typeof document === "undefined") return;
    document.querySelectorAll("audio.ai-va-message-audio").forEach((node) => {
      if (node !== audioRef.current && !node.paused) node.pause();
    });
  }, []);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    pauseOtherAssistantAudio();
    try {
      await audio.play();
    } catch (_error) {
      // Browsers may block autoplay. The same visible player remains ready for
      // an explicit user click instead of spawning a detached Audio instance.
    }
  }, [pauseOtherAssistantAudio]);

  useEffect(() => {
    autoplayAttemptedRef.current = false;
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, [src]);

  useEffect(() => {
    if (!autoPlay || !src || autoplayAttemptedRef.current) return;
    autoplayAttemptedRef.current = true;
    const timer = window.setTimeout(() => {
      play();
      onAutoPlayAttempted?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [autoPlay, onAutoPlayAttempted, play, src]);

  const togglePlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) play();
    else audio.pause();
  };

  const stopPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setCurrentTime(0);
  };

  const restartPlayback = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    play();
  };

  const toggleLoop = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextLoop = !loop;
    audio.loop = nextLoop;
    setLoop(nextLoop);
  };

  const seek = (event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextTime = Number(event.target.value);
    if (!Number.isFinite(nextTime)) return;
    audio.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const changeVolume = (event) => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextVolume = Math.min(1, Math.max(0, Number(event.target.value)));
    audio.volume = nextVolume;
    audio.muted = nextVolume === 0 ? true : muted;
    setVolume(nextVolume);
    if (nextVolume === 0) setMuted(true);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const nextMuted = !muted;
    audio.muted = nextMuted;
    setMuted(nextMuted);
  };

  return (
    <div className="ai-va-audio-player">
      <audio
        ref={audioRef}
        className="ai-va-message-audio"
        src={src}
        preload="metadata"
        loop={loop}
        muted={muted}
        onLoadedMetadata={(event) => {
          const nextDuration = Number(event.currentTarget.duration);
          setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
          event.currentTarget.volume = volume;
        }}
        onDurationChange={(event) => {
          const nextDuration = Number(event.currentTarget.duration);
          if (Number.isFinite(nextDuration)) setDuration(nextDuration);
        }}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime || 0)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          if (!loop) {
            setIsPlaying(false);
            setCurrentTime(duration || 0);
          }
        }}
      />

      <div className="ai-va-audio-main-controls">
        <button
          type="button"
          onClick={togglePlayback}
          title={isPlaying ? text.pauseAudio || "Pause" : text.playAudio || "Play"}
          aria-label={isPlaying ? text.pauseAudio || "Pause" : text.playAudio || "Play"}
        >
          {isPlaying ? <PauseRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
        </button>
        <button
          type="button"
          onClick={stopPlayback}
          title={text.stopAudio || "Stop"}
          aria-label={text.stopAudio || "Stop"}
        >
          <StopRoundedIcon fontSize="small" />
        </button>
        <button
          type="button"
          onClick={restartPlayback}
          title={text.restartAudio || "Restart"}
          aria-label={text.restartAudio || "Restart"}
        >
          <RestartAltRoundedIcon fontSize="small" />
        </button>
        <button
          type="button"
          className={loop ? "is-active" : ""}
          onClick={toggleLoop}
          title={loop ? text.disableRepeat || "Disable repeat" : text.enableRepeat || "Repeat"}
          aria-label={loop ? text.disableRepeat || "Disable repeat" : text.enableRepeat || "Repeat"}
          aria-pressed={loop}
        >
          <RepeatRoundedIcon fontSize="small" />
        </button>
      </div>

      <div className="ai-va-audio-progress-row">
        <span>{formatAudioTime(currentTime)}</span>
        <input
          type="range"
          min="0"
          max={duration > 0 ? duration : 0}
          step="0.1"
          value={duration > 0 ? Math.min(currentTime, duration) : 0}
          onChange={seek}
          disabled={!duration}
          aria-label={text.seekAudio || "Seek audio"}
        />
        <span>{formatAudioTime(duration)}</span>
      </div>

      <div className="ai-va-audio-volume-row">
        <button
          type="button"
          onClick={toggleMute}
          title={muted ? text.unmuteAudio || "Unmute" : text.muteAudio || "Mute"}
          aria-label={muted ? text.unmuteAudio || "Unmute" : text.muteAudio || "Mute"}
        >
          {muted || volume === 0 ? <VolumeOffRoundedIcon fontSize="small" /> : <VolumeUpRoundedIcon fontSize="small" />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={volume}
          onChange={changeVolume}
          aria-label={text.audioVolume || "Audio volume"}
        />
      </div>
    </div>
  );
}

function MessageBubble({ message, text, autoPlayAudio = false, onAutoPlayAttempted }) {
  const isUser = message.role === "user";
  const candidates = Array.isArray(message?.clarification?.candidates)
    ? message.clarification.candidates
    : [];
  const sources = Array.isArray(message.sources) ? message.sources : [];
  const readableSources = sources
    .map((source) => {
      if (typeof source === "string") return source.trim();
      if (!source || typeof source !== "object") return "";
      return String(source.title || source.name || source.source || source.url || source.id || "").trim();
    })
    .filter(Boolean);

  return (
    <div className={`ai-va-message-row ${isUser ? "is-user" : "is-assistant"}`}>
      <div className="ai-va-message-bubble">
        <div className="ai-va-message-meta">
          <span>{isUser ? text.userLabel : text.assistantLabel}</span>
          {message.duration ? <span>{formatDuration(message.duration)}</span> : null}
        </div>

        <p>{message.text}</p>

        {message.needsClarification ? (
          <>
            <span className="ai-va-message-chip">{text.clarification}</span>

            {candidates.length > 0 ? (
              <div className="ai-va-clarification-panel">
                <div className="ai-va-clarification-label">{text.candidates}</div>
                <div className="ai-va-clarification-list">
                  {candidates.map((candidate) => (
                    <span key={candidate} className="ai-va-clarification-candidate">
                      {candidate}
                    </span>
                  ))}
                </div>
                <p className="ai-va-clarification-hint">{text.clarificationHint}</p>
              </div>
            ) : null}
          </>
        ) : null}

        {message.audioSource ? (
          <AssistantAudioPlayer
            src={message.audioSource}
            text={text}
            autoPlay={autoPlayAudio}
            onAutoPlayAttempted={onAutoPlayAttempted}
          />
        ) : null}

        {readableSources.length > 0 ? (
          <div className="ai-va-message-sources">
            <span>{text.sources}</span>
            {readableSources.slice(0, 4).map((source, index) => (
              <span key={`${source}-${index}`}>{source}</span>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function AiVoiceAssistant() {
  const { settings } = useAiVoiceAssistantSettings();
  const { org, permissions, user } = useAuth();
  const { lang } = useContext(LanguageContext);
  const text = useMemo(
    () => ({
      ...DEFAULT_TEXT,
      ...(aiVoiceAssistantTranslations[lang]?.aiVoiceAssistant || {}),
    }),
    [lang]
  );
  const isRtl = ["ar", "ur"].includes(lang);
  const canUseVoiceCommand = Boolean(permissions?.ai_voice_command);
  const canUseAssistantChat = Boolean(permissions?.ai_assistant_chat);
  const canUseAssistant = canUseVoiceCommand || canUseAssistantChat;
  const availableAssistantModes = useMemo(
    () => [
      ...(canUseVoiceCommand ? ["voice"] : []),
      ...(canUseAssistantChat ? ["chat"] : []),
    ],
    [canUseAssistantChat, canUseVoiceCommand]
  );
  const showModeTabs = availableAssistantModes.length > 1;

  const [open, setOpen] = useState(false);
  const [conversation, setConversation] = useState(() => ({
    key: "",
    sessionId: "",
    messages: [],
  }));
  const [recorderState, setRecorderState] = useState("ready");
  const [recordedMs, setRecordedMs] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewSource, setPreviewSource] = useState("");
  const [previewLabel, setPreviewLabel] = useState("");
  const [assistantMode, setAssistantMode] = useState("voice");
  const [chatInput, setChatInput] = useState("");
  const [chatState, setChatState] = useState("ready");
  const [chatVoiceConfig, setChatVoiceConfig] = useState(() => normalizeAiChatVoiceReplyConfig());
  const [selectedChatVoice, setSelectedChatVoice] = useState("");
  const [userVoiceRepliesEnabled, setUserVoiceRepliesEnabled] = useState(true);
  const [voicePreferenceSaving, setVoicePreferenceSaving] = useState(false);
  const [autoPlayAudioMessageId, setAutoPlayAudioMessageId] = useState("");
  const activeAssistantMode = availableAssistantModes.includes(assistantMode)
    ? assistantMode
    : availableAssistantModes[0] || "voice";

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const discardRecordingRef = useRef(false);
  const previewUrlRef = useRef("");
  const previewAudioRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const uploadAbortRef = useRef(null);
  const chatAbortRef = useRef(null);
  const chatHistoryAbortRef = useRef(null);
  const chatHistoryLoadedKeyRef = useRef("");

  const organizationId = org?.id;
  const subscriberId = user?.subid || org?.subid;
  const conversationKey = useMemo(
    () => getConversationStorageKey(user?.id, organizationId),
    [organizationId, user?.id]
  );

  const savedChatVoicePreference = String(user?.settings?.aiChatVoice?.voice || "").trim().toLowerCase();
  const savedVoiceReplyPreference = user?.settings?.aiChatVoice?.voiceReplyEnabled;
  const effectiveVoiceRepliesEnabled =
    chatVoiceConfig.voiceReply !== false && userVoiceRepliesEnabled !== false;
  const availableChatVoices = useMemo(
    () => (Array.isArray(chatVoiceConfig.voices) ? chatVoiceConfig.voices : []),
    [chatVoiceConfig.voices]
  );

  useEffect(() => {
    setSelectedChatVoice((current) =>
      resolveAiChatVoiceForUser(chatVoiceConfig, current || savedChatVoicePreference)
    );
  }, [chatVoiceConfig, savedChatVoicePreference]);

  useEffect(() => {
    setUserVoiceRepliesEnabled(
      typeof savedVoiceReplyPreference === "boolean" ? savedVoiceReplyPreference : true
    );
  }, [savedVoiceReplyPreference, user?.id]);

  useEffect(() => {
    if (effectiveVoiceRepliesEnabled || typeof document === "undefined") return;
    setAutoPlayAudioMessageId("");
    document.querySelectorAll("audio.ai-va-message-audio").forEach((audio) => {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch (_error) {
        // Some browsers reject currentTime changes while metadata is not ready.
      }
    });
  }, [effectiveVoiceRepliesEnabled]);

  const changeVoiceRepliesEnabled = useCallback(async (enabled) => {
    const nextEnabled = Boolean(enabled);
    if (nextEnabled === userVoiceRepliesEnabled || voicePreferenceSaving) return;
    const previousEnabled = userVoiceRepliesEnabled;
    setUserVoiceRepliesEnabled(nextEnabled);
    setVoicePreferenceSaving(true);
    setErrorMessage("");
    try {
      await saveAiChatVoicePreference({ voiceReplyEnabled: nextEnabled });
    } catch (error) {
      setUserVoiceRepliesEnabled(previousEnabled);
      setErrorMessage(getReadableError(error, text));
    } finally {
      setVoicePreferenceSaving(false);
    }
  }, [text, userVoiceRepliesEnabled, voicePreferenceSaving]);

  const changeChatVoice = useCallback(async (voice) => {
    const nextVoice = resolveAiChatVoiceForUser(chatVoiceConfig, voice);
    if (!nextVoice || nextVoice === selectedChatVoice || voicePreferenceSaving) return;
    const previousVoice = selectedChatVoice;
    setSelectedChatVoice(nextVoice);
    setVoicePreferenceSaving(true);
    setErrorMessage("");
    try {
      await saveAiChatVoicePreference({ voice: nextVoice });
    } catch (error) {
      setSelectedChatVoice(previousVoice || resolveAiChatVoiceForUser(chatVoiceConfig, savedChatVoicePreference));
      setErrorMessage(getReadableError(error, text));
    } finally {
      setVoicePreferenceSaving(false);
    }
  }, [chatVoiceConfig, savedChatVoicePreference, selectedChatVoice, text, voicePreferenceSaving]);

  useEffect(() => {
    if (!canUseAssistantChat || !organizationId) {
      setChatVoiceConfig(normalizeAiChatVoiceReplyConfig());
      return undefined;
    }

    let active = true;
    fetchAiChatVoiceReplyConfig(organizationId)
      .then((config) => {
        if (active) setChatVoiceConfig(config);
      })
      .catch((error) => {
        console.error("Failed to load AI chat voice configuration:", error);
        if (active) setChatVoiceConfig(normalizeAiChatVoiceReplyConfig());
      });

    return () => {
      active = false;
    };
  }, [canUseAssistantChat, organizationId]);

  useEffect(() => {
    const handleConfigUpdated = () => {
      if (!canUseAssistantChat || !organizationId) return;
      fetchAiChatVoiceReplyConfig(organizationId)
        .then((config) => setChatVoiceConfig(config))
        .catch((error) => console.error("Failed to refresh AI chat voice configuration:", error));
    };
    window.addEventListener(AI_CHAT_VOICE_CONFIG_UPDATED_EVENT, handleConfigUpdated);
    return () => window.removeEventListener(AI_CHAT_VOICE_CONFIG_UPDATED_EVENT, handleConfigUpdated);
  }, [canUseAssistantChat, organizationId]);

  useEffect(() => {
    chatHistoryAbortRef.current?.abort();
    chatHistoryLoadedKeyRef.current = "";
    setConversation(readConversation(conversationKey));
  }, [conversationKey]);

  useEffect(() => {
    if (conversation.key !== conversationKey || typeof window === "undefined") return;

    try {
      window.sessionStorage.setItem(
        conversationKey,
        JSON.stringify({
          sessionId: conversation.sessionId,
          // AI voice replies can contain very large base64 audio payloads. Keep
          // them in memory for playback, but never persist data/blob URLs in
          // sessionStorage or a few replies can exceed the browser quota.
          messages: conversation.messages.map((message) => ({
            ...message,
            ...(typeof message.audioSource === "string" && /^(data:|blob:)/i.test(message.audioSource)
              ? { audioSource: "" }
              : {}),
          })),
        })
      );
    } catch (error) {
      console.error("Failed to save AI voice assistant conversation:", error);
    }
  }, [conversation, conversationKey]);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  const messages = useMemo(
    () => (conversation.key === conversationKey ? conversation.messages : []),
    [conversation.key, conversation.messages, conversationKey]
  );
  const sessionId = useMemo(
    () => (conversation.key === conversationKey ? normalizeChatSessionId(conversation.sessionId) : ""),
    [conversation.key, conversation.sessionId, conversationKey]
  );
  const visibleMessages = useMemo(
    () =>
      messages.filter((message) =>
        activeAssistantMode === "chat" ? message.mode === "chat" : message.mode !== "chat"
      ),
    [activeAssistantMode, messages]
  );
  const hasPreview = Boolean(audioBlob && previewUrl);
  const isRecording = recorderState === "recording";
  const isProcessing = recorderState === "processing";
  const isChatProcessing = chatState === "processing";
  const position = settings.position || "bottom-right";
  const isLeftPosition = position.endsWith("left");
  const shellClassName = [
    "ai-va-shell",
    `is-${position}`,
    isLeftPosition ? "is-left" : "is-right",
    open ? "is-open" : "is-closed",
    isRtl ? "is-rtl" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const statusText = useMemo(() => {
    if (activeAssistantMode === "chat") {
      if (errorMessage && chatState === "error") return errorMessage;
      if (recorderState === "recording") return text.recording;
      if (recorderState === "preview") return text.preview;
      if (chatState === "processing" || recorderState === "processing") return text.chatThinking;
      return text.chatReady;
    }

    if (errorMessage && recorderState === "error") return errorMessage;
    if (recorderState === "recording") return text.recording;
    if (recorderState === "preview") {
      return previewSource === "upload" ? text.uploadReady : text.preview;
    }
    if (recorderState === "processing") return text.processing;
    return text.ready;
  }, [activeAssistantMode, chatState, errorMessage, previewSource, recorderState, text]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }

    setAudioBlob(null);
    setPreviewUrl("");
    setRecordedMs(0);
    setIsPreviewPlaying(false);
    setPreviewSource("");
    setPreviewLabel("");
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    recorder.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    discardRecordingRef.current = true;
    setErrorMessage("");

    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
      return;
    }

    stopTimer();
    stopStream();
    clearPreview();
    setRecorderState("ready");
  }, [clearPreview, stopStream, stopTimer]);

  const updateConversation = useCallback(
    (updater) => {
      setConversation((prev) => {
        if (prev.key !== conversationKey) return prev;
        const next = typeof updater === "function" ? updater(prev) : updater;
        return { ...prev, ...next };
      });
    },
    [conversationKey]
  );

  useEffect(() => {
    if (!canUseAssistantChat || !subscriberId || !organizationId) return undefined;
    if (conversation.key !== conversationKey) return undefined;
    if (chatHistoryLoadedKeyRef.current === conversationKey) return undefined;

    chatHistoryLoadedKeyRef.current = conversationKey;
    const controller = new AbortController();
    chatHistoryAbortRef.current = controller;

    const loadHistory = async () => {
      try {
        let response;

        try {
          response = await fetchAssistantChatHistory({
            sessionId,
            subscriberId,
            organizationId,
            signal: controller.signal,
          });
        } catch (error) {
          const status = error?.response?.status;
          if (!sessionId || status !== 404) throw error;

          // A locally cached session can be stale/deleted. In that case ask the
          // server for this token's latest valid conversation instead.
          response = await fetchAssistantChatHistory({
            subscriberId,
            organizationId,
            signal: controller.signal,
          });
        }

        if (controller.signal.aborted) return;

        const resolvedSessionId = normalizeChatSessionId(response.sessionId);
        const historyMessages = chatHistoryToMessages(
          response.history,
          resolvedSessionId || sessionId
        );

        updateConversation((prev) => ({
          sessionId: resolvedSessionId || prev.sessionId || "",
          messages: [
            ...prev.messages.filter((message) => message.mode !== "chat"),
            ...historyMessages,
          ],
        }));
      } catch (error) {
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
        // History recovery must not prevent the user from starting a new chat.
        // A first message will create and send a fresh session id.
        console.error("Failed to restore AI chat history:", error);
      } finally {
        if (chatHistoryAbortRef.current === controller) {
          chatHistoryAbortRef.current = null;
        }
      }
    };

    loadHistory();

    return () => {
      controller.abort();
    };
  }, [
    canUseAssistantChat,
    sessionId,
    conversation.key,
    conversationKey,
    organizationId,
    subscriberId,
    updateConversation,
  ]);

  const applyAudioPreview = useCallback(
    async (blob, nextSource, nextLabel = "") => {
      if (!blob || !blob.size) {
        setRecorderState("error");
        setErrorMessage(text.invalidAudioFile);
        return;
      }

      clearPreview();
      setErrorMessage("");

      const url = window.URL.createObjectURL(blob);
      const durationMs = await readAudioDuration(url);

      setAudioBlob(blob);
      setPreviewUrl(url);
      setPreviewSource(nextSource);
      setPreviewLabel(nextLabel);
      setRecordedMs(durationMs);
      setRecorderState("preview");
    },
    [clearPreview, text.invalidAudioFile]
  );

  const startRecording = useCallback(async () => {
    if (isProcessing) return;

    if (!window.navigator?.mediaDevices?.getUserMedia || typeof window.MediaRecorder === "undefined") {
      setRecorderState("error");
      setErrorMessage(text.noAudioSupport);
      return;
    }

    clearPreview();
    setErrorMessage("");
    discardRecordingRef.current = false;
    chunksRef.current = [];

    try {
      const stream = await window.navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const mimeType = getPreferredMimeType();
      const recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        setRecorderState("error");
        setErrorMessage(getReadableError(event.error, text));
      };

      recorder.onstop = () => {
        stopTimer();
        stopStream();
        recorderRef.current = null;

        const chunks = chunksRef.current;
        chunksRef.current = [];

        if (discardRecordingRef.current) {
          discardRecordingRef.current = false;
          clearPreview();
          setRecorderState("ready");
          return;
        }

        const blob = new Blob(chunks, {
          type: recorder.mimeType || mimeType || "audio/webm",
        });

        if (!blob.size) {
          setRecorderState("error");
          setErrorMessage(text.recordingEmpty);
          return;
        }

        applyAudioPreview(blob, "recording");
      };

      startedAtRef.current = Date.now();
      setRecordedMs(0);
      recorder.start(250);
      setRecorderState("recording");

      timerRef.current = window.setInterval(() => {
        setRecordedMs(Date.now() - startedAtRef.current);
      }, 250);
    } catch (error) {
      stopTimer();
      stopStream();
      setRecorderState("error");
      setErrorMessage(getReadableError(error, text));
    }
  }, [applyAudioPreview, clearPreview, isProcessing, stopStream, stopTimer, text]);

  const togglePreviewPlayback = useCallback(async () => {
    const audio = previewAudioRef.current;
    if (!audio || !previewUrl) return;

    try {
      if (isPreviewPlaying) {
        audio.pause();
        setIsPreviewPlaying(false);
      } else {
        await audio.play();
        setIsPreviewPlaying(true);
      }
    } catch (error) {
      setErrorMessage(getReadableError(error, text));
      setRecorderState("error");
    }
  }, [isPreviewPlaying, previewUrl, text]);

  const openUploadPicker = useCallback(() => {
    if (isRecording || isProcessing) return;
    fileInputRef.current?.click();
  }, [isProcessing, isRecording]);

  const handleAudioFileSelected = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const looksLikeAudio =
        String(file.type || "").startsWith("audio/") ||
        /\.(m4a|mp3|wav|ogg|webm|aac|flac)$/i.test(file.name || "");

      if (!looksLikeAudio) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setRecorderState("error");
        setErrorMessage(text.invalidAudioFile);
        return;
      }

      await applyAudioPreview(file, "upload", file.name || text.previewFile);
    },
    [applyAudioPreview, text]
  );

  const submitPreview = useCallback(async () => {
    if (!audioBlob || isProcessing) return;

    if (!organizationId) {
      setRecorderState("error");
      setErrorMessage(text.organizationMissing);
      return;
    }

    // Voice-command and RAG voice-chat now share one session_id. If restoring
    // an older RAG session is still in flight, this explicit user action wins.
    chatHistoryAbortRef.current?.abort();
    chatHistoryLoadedKeyRef.current = conversationKey;

    const activeSessionId = sessionId || createUuid();
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setErrorMessage("");
    setRecorderState("processing");

    try {
      const response = await processVoiceCommand({
        audioBlob,
        sessionId: activeSessionId,
        language: lang,
        organizationId,
        userId: user?.id,
        signal: controller.signal,
      });

      const nextSessionId = normalizeChatSessionId(response.sessionId) || activeSessionId;
      const userMessage = {
        id: createId(),
        role: "user",
        text: response.transcript || text.voiceMessage,
        duration: recordedMs,
        mode: "voice",
        createdAt: new Date().toISOString(),
      };
      const assistantMessage = {
        id: createId(),
        role: "assistant",
        text:
          response.responseText ||
          (response.needsClarification ? text.clarifyRequest : text.assistantFallback),
        audioSource: response.audioSource,
        needsClarification: response.needsClarification,
        clarification: response.clarification,
        mode: "voice",
        createdAt: new Date().toISOString(),
      };

      updateConversation((prev) => ({
        sessionId: nextSessionId,
        messages: [...prev.messages, userMessage, assistantMessage],
      }));
      setAutoPlayAudioMessageId(
        effectiveVoiceRepliesEnabled && response.audioSource ? assistantMessage.id : ""
      );

      clearPreview();
      setRecorderState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setRecorderState("preview");
      setErrorMessage(getReadableError(error, text));
    } finally {
      uploadAbortRef.current = null;
    }
  }, [
    audioBlob,
    clearPreview,
    conversationKey,
    isProcessing,
    lang,
    organizationId,
    recordedMs,
    sessionId,
    text,
    updateConversation,
    user?.id,
  ]);

  const submitChatVoicePreview = useCallback(async () => {
    if (!audioBlob || isProcessing || isChatProcessing) return;

    if (!organizationId) {
      setChatState("error");
      setErrorMessage(text.organizationMissing);
      return;
    }

    chatHistoryAbortRef.current?.abort();
    chatHistoryLoadedKeyRef.current = conversationKey;

    const activeSessionId = sessionId || createUuid();
    const controller = new AbortController();
    chatAbortRef.current = controller;
    setErrorMessage("");
    setChatState("processing");
    setRecorderState("processing");

    try {
      const response = await sendAssistantVoiceChatMessage({
        audioBlob,
        sessionId: activeSessionId,
        subscriberId,
        organizationId,
        language: lang,
        voiceReply: effectiveVoiceRepliesEnabled,
        voice: effectiveVoiceRepliesEnabled ? selectedChatVoice || chatVoiceConfig.defaultVoice : "",
        signal: controller.signal,
      });

      const nextSessionId = normalizeChatSessionId(response.sessionId) || activeSessionId;
      const userMessage = {
        id: createId(),
        role: "user",
        text: response.transcript || text.voiceMessage,
        duration: recordedMs,
        mode: "chat",
        createdAt: new Date().toISOString(),
      };
      const assistantMessage = {
        id: createId(),
        role: "assistant",
        text: response.text || text.chatFallback,
        sources: response.sources,
        audioSource: effectiveVoiceRepliesEnabled ? response.audioSource || "" : "",
        mode: "chat",
        createdAt: new Date().toISOString(),
      };

      updateConversation((prev) => ({
        sessionId: nextSessionId,
        messages: [...prev.messages, userMessage, assistantMessage],
      }));

      clearPreview();
      setRecorderState("ready");
      setChatState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setRecorderState("preview");
      setChatState("error");
      setErrorMessage(getReadableError(error, text));
    } finally {
      if (chatAbortRef.current === controller) {
        chatAbortRef.current = null;
      }
    }
  }, [
    audioBlob,
    chatVoiceConfig,
    effectiveVoiceRepliesEnabled,
    selectedChatVoice,
    clearPreview,
    conversationKey,
    isChatProcessing,
    isProcessing,
    lang,
    organizationId,
    recordedMs,
    sessionId,
    subscriberId,
    text,
    updateConversation,
  ]);

  const submitChatMessage = useCallback(async () => {
    const prompt = chatInput.trim();

    if (!prompt || isChatProcessing) return;

    if (!organizationId) {
      setChatState("error");
      setErrorMessage(text.organizationMissing);
      return;
    }

    // If history restoration is still in flight, this user action wins. It
    // must not be allowed to overwrite the freshly created first-message session.
    chatHistoryAbortRef.current?.abort();
    chatHistoryLoadedKeyRef.current = conversationKey;

    const activeChatSessionId = sessionId || createUuid();
    const controller = new AbortController();
    chatAbortRef.current = controller;
    setChatInput("");
    setErrorMessage("");
    setChatState("processing");

    const userMessage = {
      id: createId(),
      role: "user",
      text: prompt,
      mode: "chat",
      createdAt: new Date().toISOString(),
    };

    updateConversation((prev) => ({
      sessionId: activeChatSessionId,
      messages: [...prev.messages, userMessage],
    }));

    try {
      const response = await sendAssistantChatMessage({
        prompt,
        sessionId: activeChatSessionId,
        subscriberId,
        organizationId,
        voiceReply: effectiveVoiceRepliesEnabled,
        voice: effectiveVoiceRepliesEnabled ? selectedChatVoice || chatVoiceConfig.defaultVoice : "",
        signal: controller.signal,
      });

      const assistantMessage = {
        id: createId(),
        role: "assistant",
        text: response.text || text.chatFallback,
        sources: response.sources,
        audioSource: effectiveVoiceRepliesEnabled ? response.audioSource || "" : "",
        mode: "chat",
        createdAt: new Date().toISOString(),
      };
      const responseSessionId = normalizeChatSessionId(response.sessionId);

      updateConversation((prev) => ({
        sessionId: responseSessionId || prev.sessionId || activeChatSessionId,
        messages: [...prev.messages, assistantMessage],
      }));
      setAutoPlayAudioMessageId(
        effectiveVoiceRepliesEnabled && response.audioSource ? assistantMessage.id : ""
      );
      setChatState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setChatState("error");
      setErrorMessage(getReadableError(error, text));
      setChatInput(prompt);
    } finally {
      if (chatAbortRef.current === controller) {
        chatAbortRef.current = null;
      }
    }
  }, [
    chatInput,
    sessionId,
    conversationKey,
    isChatProcessing,
    organizationId,
    subscriberId,
    chatVoiceConfig,
    effectiveVoiceRepliesEnabled,
    selectedChatVoice,
    text,
    updateConversation,
  ]);

  const handleChatKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" || event.shiftKey) return;
      event.preventDefault();
      submitChatMessage();
    },
    [submitChatMessage]
  );

  const switchAssistantMode = useCallback(
    (mode) => {
      if (mode === "voice" && !canUseVoiceCommand) return;
      if (mode === "chat" && !canUseAssistantChat) return;
      if (mode === assistantMode) return;
      cancelRecording();
      setAssistantMode(mode);
      setErrorMessage("");
    },
    [assistantMode, canUseAssistantChat, canUseVoiceCommand, cancelRecording]
  );

  const handlePrimaryAction = useCallback(() => {
    if (isProcessing) return;
    if (isRecording) {
      stopRecording();
      return;
    }
    if (hasPreview) {
      submitPreview();
      return;
    }
    startRecording();
  }, [hasPreview, isProcessing, isRecording, startRecording, stopRecording, submitPreview]);

  const deleteCurrentChatConversation = useCallback(async () => {
    if (activeAssistantMode !== "chat" || !canUseAssistantChat || isChatProcessing || isProcessing) return;
    if (!sessionId || !organizationId) return;

    if (typeof window !== "undefined" && !window.confirm(text.deleteConversationConfirm || "Delete this conversation permanently?")) {
      return;
    }

    cancelRecording();
    chatAbortRef.current?.abort();
    chatHistoryAbortRef.current?.abort();
    chatHistoryLoadedKeyRef.current = conversationKey;
    const controller = new AbortController();
    chatAbortRef.current = controller;
    setErrorMessage("");
    setChatInput("");
    setChatState("processing");

    try {
      await deleteAssistantChat({
        sessionId,
        subscriberId,
        organizationId,
        signal: controller.signal,
      });
      updateConversation((prev) => ({
        sessionId: "",
        messages: prev.messages.filter((message) => message.mode !== "chat"),
      }));
      setChatState("ready");
      setRecorderState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setChatState("error");
      setErrorMessage(getReadableError(error, text));
    } finally {
      if (chatAbortRef.current === controller) chatAbortRef.current = null;
    }
  }, [
    activeAssistantMode,
    canUseAssistantChat,
    cancelRecording,
    conversationKey,
    isChatProcessing,
    isProcessing,
    organizationId,
    sessionId,
    subscriberId,
    text,
    updateConversation,
  ]);

  const resetConversation = useCallback(async () => {
    cancelRecording();
    setErrorMessage("");

    if (!organizationId) {
      const setter = activeAssistantMode === "chat" ? setChatState : setRecorderState;
      setter("error");
      setErrorMessage(text.organizationMissing);
      return;
    }

    if (activeAssistantMode === "chat" && canUseAssistantChat) {
      chatAbortRef.current?.abort();
      chatHistoryAbortRef.current?.abort();
      chatHistoryLoadedKeyRef.current = conversationKey;

      const controller = new AbortController();
      chatAbortRef.current = controller;
      setChatState("processing");
      setChatInput("");

      try {
        const response = await startNewAssistantChat({
          sessionId,
          subscriberId,
          organizationId,
          signal: controller.signal,
        });
        const nextSessionId = normalizeChatSessionId(response.sessionId) || createUuid();

        // A new RAG session must also become the shared session_id used by later
        // voice commands / voice chat so the context bridge cannot point at the
        // previous conversation.
        updateConversation(() => ({
          sessionId: nextSessionId,
          messages: [],
        }));
        setRecorderState("ready");
        setChatState("ready");
      } catch (error) {
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
        setChatState("error");
        setErrorMessage(getReadableError(error, text));
      } finally {
        if (chatAbortRef.current === controller) {
          chatAbortRef.current = null;
        }
      }

      return;
    }

    if (!sessionId) {
      updateConversation((prev) => ({
        messages: prev.messages.filter((message) => message.mode === "chat"),
      }));
      setRecorderState("ready");
      return;
    }

    const controller = new AbortController();
    uploadAbortRef.current = controller;
    setRecorderState("processing");

    try {
      await resetVoiceSession({ sessionId, signal: controller.signal });
      updateConversation((prev) => ({
        messages: prev.messages.filter((message) => message.mode === "chat"),
      }));
      setRecorderState("ready");
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
      setRecorderState("error");
      setErrorMessage(getReadableError(error, text));
    } finally {
      if (uploadAbortRef.current === controller) {
        uploadAbortRef.current = null;
      }
    }
  }, [
    activeAssistantMode,
    canUseAssistantChat,
    cancelRecording,
    conversationKey,
    organizationId,
    sessionId,
    subscriberId,
    text,
    updateConversation,
  ]);

  useEffect(() => {
    if (!settings.enabled) {
      setOpen(false);
      cancelRecording();
    }
  }, [cancelRecording, settings.enabled]);

  useEffect(() => {
    const nextMode = availableAssistantModes[0];

    if (!nextMode) {
      return;
    }

    if (!availableAssistantModes.includes(assistantMode)) {
      if (assistantMode === "voice") {
        cancelRecording();
      }
      if (assistantMode === "chat") {
        chatAbortRef.current?.abort();
        setChatState("ready");
      }
      setAssistantMode(nextMode);
    }
  }, [assistantMode, availableAssistantModes, cancelRecording]);

  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ block: "end" });
  }, [open, visibleMessages]);

  useEffect(() => {
    return () => {
      uploadAbortRef.current?.abort();
      chatAbortRef.current?.abort();
      chatHistoryAbortRef.current?.abort();
      discardRecordingRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      stopTimer();
      stopStream();
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, [stopStream, stopTimer]);

  if (!canUseAssistant || !settings.enabled) return null;

  const primaryTitle = isRecording
    ? text.stop
    : hasPreview
      ? text.send
      : text.start;
  const previewTitle = getPreviewTitle(previewSource, previewLabel, text);

  return (
    <div className={shellClassName} dir={isRtl ? "rtl" : "ltr"}>
      {open ? (
        <section className="ai-va-panel" aria-label={text.title}>
          <div className="ai-va-panel-header">
            <div className="ai-va-title-block">
              <span className="ai-va-title-icon" aria-hidden="true">
                {activeAssistantMode === "chat" ? (
                  <ChatBubbleOutlineRoundedIcon fontSize="small" />
                ) : (
                  <AutoAwesomeRoundedIcon fontSize="small" />
                )}
              </span>
              <div>
                <h2>{text.title}</h2>
                <span className={`ai-va-status is-${recorderState}`}>
                  <span className="ai-va-status-dot" />
                  {statusText}
                </span>
              </div>
            </div>

            <div className="ai-va-header-actions">
              <button
                type="button"
                onClick={resetConversation}
                disabled={isChatProcessing || isProcessing || isRecording}
                title={activeAssistantMode === "chat" ? (text.newConversation || "New conversation") : text.clear}
                aria-label={activeAssistantMode === "chat" ? (text.newConversation || "New conversation") : text.clear}
              >
                {activeAssistantMode === "chat" ? (
                  <AddCommentRoundedIcon fontSize="small" />
                ) : (
                  <RestartAltRoundedIcon fontSize="small" />
                )}
              </button>
              {activeAssistantMode === "chat" ? (
                <button
                  type="button"
                  onClick={deleteCurrentChatConversation}
                  disabled={!sessionId || isChatProcessing || isProcessing || isRecording}
                  title={text.deleteConversation || "Delete conversation"}
                  aria-label={text.deleteConversation || "Delete conversation"}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </button>
              ) : null}
              <button type="button" onClick={() => setOpen(false)} title={text.close} aria-label={text.close}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>
          </div>

          {showModeTabs ? (
            <div className="ai-va-mode-tabs" role="tablist" aria-label={text.modeLabel}>
              <button
                type="button"
                className={activeAssistantMode === "voice" ? "is-active" : ""}
                onClick={() => switchAssistantMode("voice")}
                disabled={isChatProcessing || isRecording || isProcessing}
                role="tab"
                aria-selected={activeAssistantMode === "voice"}
              >
                <KeyboardVoiceRoundedIcon fontSize="small" />
                <span>{text.voiceMode}</span>
              </button>
              <button
                type="button"
                className={activeAssistantMode === "chat" ? "is-active" : ""}
                onClick={() => switchAssistantMode("chat")}
                disabled={isRecording || isProcessing}
                role="tab"
                aria-selected={activeAssistantMode === "chat"}
              >
                <ChatBubbleOutlineRoundedIcon fontSize="small" />
                <span>{text.chatMode}</span>
              </button>
            </div>
          ) : null}

          <div className="ai-va-conversation">
            {visibleMessages.length === 0 ? (
              <div className="ai-va-empty">
                <div className="ai-va-empty-orb" aria-hidden="true">
                  {activeAssistantMode === "chat" ? <ChatBubbleOutlineRoundedIcon /> : <GraphicEqRoundedIcon />}
                </div>
                <h3>{activeAssistantMode === "chat" ? text.chatEmptyTitle : text.emptyTitle}</h3>
                <p>{activeAssistantMode === "chat" ? text.chatEmptySubtitle : text.emptySubtitle}</p>
              </div>
            ) : (
              visibleMessages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  text={text}
                  autoPlayAudio={message.id === autoPlayAudioMessageId}
                  onAutoPlayAttempted={() => {
                    if (message.id === autoPlayAudioMessageId) setAutoPlayAudioMessageId("");
                  }}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {activeAssistantMode === "chat" ? (
            <form
              className="ai-va-composer ai-va-chat-composer"
              onSubmit={(event) => {
                event.preventDefault();
                submitChatMessage();
              }}
            >
              {errorMessage ? <div className="ai-va-error">{errorMessage}</div> : null}

              {chatVoiceConfig.voiceReply !== false ? (
                <div className="ai-va-voice-settings">
                  <div className="ai-va-voice-reply-toggle">
                    <div>
                      <strong>{text.voiceReplies || "Voice replies"}</strong>
                      <span>{text.voiceRepliesDescription || "Choose whether AI answers should include audio."}</span>
                    </div>
                    <label className="ai-va-switch">
                      <input
                        type="checkbox"
                        checked={userVoiceRepliesEnabled}
                        onChange={(event) => changeVoiceRepliesEnabled(event.target.checked)}
                        disabled={voicePreferenceSaving || isChatProcessing || isProcessing}
                      />
                      <span aria-hidden="true" />
                    </label>
                  </div>

                  {effectiveVoiceRepliesEnabled && availableChatVoices.length ? (
                    <div className="ai-va-voice-choice">
                      <label>
                        <span>{text.replyVoice || "Reply voice"}</span>
                        <select
                          value={selectedChatVoice || ""}
                          onChange={(event) => changeChatVoice(event.target.value)}
                          disabled={voicePreferenceSaving || isChatProcessing || isProcessing}
                        >
                          {availableChatVoices.map((voice) => (
                            <option key={voice.value} value={voice.value}>
                              {voice.label || voice.value}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="ai-va-voice-preview-disabled"
                        disabled
                        title={text.voicePreviewUnavailable || "Voice preview will be available when the AI preview endpoint is ready."}
                      >
                        <VolumeUpRoundedIcon fontSize="small" />
                        <span>{text.previewVoice || "Preview"}</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <label className="ai-va-chat-input-shell">
                <span className="ai-va-chat-input-label">{text.chatInputLabel}</span>
                <textarea
                  value={chatInput}
                  onChange={(event) => setChatInput(event.target.value)}
                  onKeyDown={handleChatKeyDown}
                  placeholder={text.chatPlaceholder}
                  rows={3}
                  disabled={isChatProcessing}
                />
              </label>

              {hasPreview ? (
                <div className="ai-va-chat-voice-preview">
                  <AudioFileRoundedIcon fontSize="small" />
                  <span>{previewTitle || text.voiceMessage}</span>
                  <span>{formatDuration(recordedMs)}</span>
                  <button
                    type="button"
                    onClick={togglePreviewPlayback}
                    disabled={isChatProcessing || isProcessing}
                    title={isPreviewPlaying ? text.pause : text.play}
                    aria-label={isPreviewPlaying ? text.pause : text.play}
                  >
                    {isPreviewPlaying ? <PauseRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />}
                  </button>
                  <button
                    type="button"
                    onClick={cancelRecording}
                    disabled={isChatProcessing || isProcessing}
                    title={text.discard}
                    aria-label={text.discard}
                  >
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </button>
                </div>
              ) : null}

              <div className="ai-va-chat-actions">
                <span>{statusText}</span>
                <div className="ai-va-chat-action-buttons">
                  <button
                    type="button"
                    className="ai-va-chat-voice-button"
                    onClick={() => {
                      if (isRecording) {
                        stopRecording();
                      } else if (hasPreview) {
                        submitChatVoicePreview();
                      } else {
                        startRecording();
                      }
                    }}
                    disabled={isChatProcessing || (isProcessing && !hasPreview)}
                    title={isRecording ? text.stop : hasPreview ? text.send : text.start}
                    aria-label={isRecording ? text.stop : hasPreview ? text.send : text.start}
                  >
                    {isProcessing ? (
                      <span className="ai-va-spinner" />
                    ) : isRecording ? (
                      <StopRoundedIcon />
                    ) : hasPreview ? (
                      <SendRoundedIcon />
                    ) : (
                      <MicNoneOutlinedIcon />
                    )}
                  </button>
                  <button
                    type="button"
                    className="ai-va-chat-upload-button"
                    onClick={openUploadPicker}
                    disabled={isRecording || isProcessing || isChatProcessing}
                    title={text.upload}
                    aria-label={text.upload}
                  >
                    <UploadFileRoundedIcon fontSize="small" />
                  </button>
                  <button
                    type="submit"
                    className="ai-va-chat-send"
                    disabled={!chatInput.trim() || isChatProcessing || isRecording || hasPreview || isProcessing}
                    title={text.sendChat}
                    aria-label={text.sendChat}
                  >
                    {isChatProcessing && !isProcessing ? <span className="ai-va-spinner" /> : <SendRoundedIcon />}
                  </button>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.flac"
                className="ai-va-file-input"
                onChange={handleAudioFileSelected}
                tabIndex={-1}
                aria-hidden="true"
              />
              <audio
                ref={previewAudioRef}
                src={previewUrl}
                onEnded={() => setIsPreviewPlaying(false)}
                onPause={() => setIsPreviewPlaying(false)}
              />
            </form>
          ) : (
          <div className="ai-va-composer">
            {errorMessage && recorderState !== "recording" ? (
              <div className="ai-va-error">{errorMessage}</div>
            ) : null}

            <div className="ai-va-upload-row">
              <button
                type="button"
                className="ai-va-upload-button"
                onClick={openUploadPicker}
                disabled={isRecording || isProcessing}
                title={text.upload}
                aria-label={text.upload}
              >
                <UploadFileRoundedIcon fontSize="small" />
                <span>{text.upload}</span>
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.m4a,.mp3,.wav,.ogg,.webm,.aac,.flac"
                className="ai-va-file-input"
                onChange={handleAudioFileSelected}
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>

            <div className="ai-va-orb-wrap" aria-hidden="true">
              <div className={`ai-va-orb is-${recorderState}`}>
                <span />
                <span />
              </div>
            </div>

            <div className="ai-va-controls">
              <button
                type="button"
                className="ai-va-mini-control"
                onClick={togglePreviewPlayback}
                disabled={!hasPreview || isProcessing}
                title={isPreviewPlaying ? text.pause : text.play}
                aria-label={isPreviewPlaying ? text.pause : text.play}
              >
                {isPreviewPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </button>

              <button
                type="button"
                className={`ai-va-record-button is-${recorderState}`}
                onClick={handlePrimaryAction}
                disabled={isProcessing}
                title={primaryTitle}
                aria-label={primaryTitle}
              >
                {isProcessing ? (
                  <span className="ai-va-spinner" />
                ) : isRecording ? (
                  <StopRoundedIcon />
                ) : hasPreview ? (
                  <SendRoundedIcon />
                ) : (
                  <MicNoneOutlinedIcon />
                )}
              </button>

              <button
                type="button"
                className="ai-va-mini-control"
                onClick={cancelRecording}
                disabled={(!hasPreview && !isRecording) || isProcessing}
                title={text.discard}
                aria-label={text.discard}
              >
                <DeleteOutlineRoundedIcon />
              </button>
            </div>

            <div className="ai-va-composer-status">
              <span>{statusText}</span>
              {(isRecording || hasPreview) && <span>{formatDuration(recordedMs)}</span>}
            </div>

            {hasPreview && previewTitle ? (
              <div className="ai-va-preview-pill" title={previewTitle}>
                <AudioFileRoundedIcon fontSize="small" />
                <span>{previewTitle}</span>
              </div>
            ) : null}

            <audio
              ref={previewAudioRef}
              src={previewUrl}
              onEnded={() => setIsPreviewPlaying(false)}
              onPause={() => setIsPreviewPlaying(false)}
            />
          </div>
          )}
        </section>
      ) : (
        <button
          type="button"
          className="ai-va-launcher"
          onClick={() => setOpen(true)}
          title={text.open}
          aria-label={text.open}
        >
          <span className="ai-va-launcher-glow" />
          <MicNoneOutlinedIcon />
          {messages.length > 0 ? <span className="ai-va-launcher-count">{messages.length}</span> : null}
        </button>
      )}
    </div>
  );
}
